/** Tailwind violet-500 — avatar palette + circle chips */
const VIOLET_500 = "#8b5cf6";

export const lightTheme = {
  colors: {
    background: "#F0F2F5",
    primary: "#0AB3B8",
    /** Deeper teal (settings / highlights), distinct from `primary` */
    primaryAccent: "#089f9f",
    primaryAltDark: "#089ba0",
    primaryDark: "#014d4e",
    accent: "#FF8A65", // lighter coral accent
    accentHover: "#ff7043",
    primaryText: "#111B21",
    secondaryText: "#667781",
    border: "#E0E0E0",
    disabled: "#CCCCCC",
    error: "#ff4d4d",
    warning: "#ffa726",
    warningTextDark: "#856404",
    warningLight: "#ffe082",
    /** Material amber bg tint */
    warningAmberTint20: "rgba(255, 193, 7, 0.2)",
    headerBorder: "rgba(255, 255, 255, 0.1)",
    headerButtonHover: "rgba(255, 255, 255, 0.2)",
    headerButtonActive: "rgba(255, 255, 255, 0.15)",
    mobileNavBackground: "#f8f9fa",
    inputPlaceholder: "#999999",
    inputFocusShadow: "rgba(10, 179, 184, 0.2)",
    /** Soft primary glow (hover cards, emphasis) */
    primaryTint10: "rgba(10, 179, 184, 0.1)",
    primaryTint04: "rgba(10, 179, 184, 0.04)",
    primaryTint05: "rgba(10, 179, 184, 0.05)",
    primaryTint15: "rgba(10, 179, 184, 0.15)",
    /** Coral wash for auth feature icons */
    accentTint16: "rgba(255, 138, 101, 0.16)",
    /** Blue wash for auth feature icons */
    blueTint12: "rgba(30, 136, 229, 0.12)",
    primaryTint40: "rgba(10, 179, 184, 0.4)",
    primaryTint80: "rgba(10, 179, 184, 0.8)",
    inputFocusShadowStrong: "rgba(10, 179, 184, 0.3)",
    inputErrorShadow: "rgba(255, 77, 77, 0.2)",
    /** Light red wash for hover states (e.g. destructive icon buttons) */
    errorOverlay10: "rgba(239, 68, 68, 0.1)",
    inputHoverShadow: "rgba(0, 0, 0, 0.04)",
    tableHeader: "#fafafa",
    /** Lavender-tinted panel (circle forms) */
    surfaceLavenderBlue: "#f8f9ff",
    /** Teal wash (drafts list surfaces) */
    surfaceTealMist: "#f0f7f7",
    /** Alice blue (avatar / input hover wells) */
    surfaceAliceBlue: "#f0f8ff",
    /** Bootstrap primary (legacy links, accents) */
    bootstrapPrimary: "#007bff",
    bootstrapPrimaryTint05: "rgba(0, 123, 255, 0.05)",
    bootstrapPrimaryTint20: "rgba(0, 123, 255, 0.2)",
    /** Material green 800 (#2E7D32, same as eventBorder) at 20% */
    eventGreenTint20: "rgba(46, 125, 50, 0.2)",
    // Semantic color palette - reusable across components
    blue: "#1e88e5",
    /** Material Design default blue (icons, links) */
    materialBlue: "#2196f3",
    /** Material blue 700 (MUI primary / selection rings) */
    materialBlue700: "#1976d2",
    /** Material blue 800 (theme swatches, strong links) */
    materialBlue800: "#1565c0",
    /** Positive / completion / success accents (Tailwind green-500) */
    green: "#22C55E",
    greenBorderLight: "#bbf7d0",
    red: "#ef4444",
    /** Tailwind red-600 (destructive text, strong error) */
    red600: "#dc2626",
    /** Material red 700 (theme picker, strong danger) */
    red700: "#d32f2f",
    /** Tailwind red-400 (audience chips, soft urgent) */
    red400: "#f87171",
    /** Material red 700 at 15% (expired card hover shadow) */
    red700Tint15: "rgba(211, 47, 47, 0.15)",
    orange: "#f59e0b",
    /** Material orange 800 (theme swatches, strong orange) */
    materialOrange800: "#ed6c02",
    teal: "#4ac5b4",
    purple: "#9F7AEA",
    /** Material purple 300 (theme swatches, light purple) */
    materialPurple300: "#ba68c8",
    pink: "#ec4899",
    pinkStrong: "#E91E63",
    yellow: "#fbbf24",
    /** Material Amber 700 (theme picker, accents) */
    amber700: "#fbc02d",
    /** Material Amber 500 (borders, highlights) */
    amber500: "#ffc107",
    /** Classic gold (featured star, awards) */
    gold: "#d4af37",
    // Navigation icon colors (matching calendar event colors)
    navCreate: "#59c47c", // green for Create Notice
    navNoticeFeed: "#fb7185", // red/pink for Notice Feed
    navManageCircles: "#fbbf24", // yellow for Manage Circles
    navTeamManagement: "#9663d1", // purple for Team Management
    /** Tailwind violet-500 (circle avatars, chips) */
    violet500: VIOLET_500,
    /** Tailwind indigo-500 (circle avatar palette) */
    indigo500: "#6366f1",
    /** Tailwind lime-500 (circle avatar palette) */
    lime500: "#84cc16",
    // Avatar color palette - vibrant colors for user avatars
    // Avoiding red (delete/danger) and blue (info notices) for semantic clarity
    avatarColors: [
      "#4ade80", // green
      "#a855f7", // purple
      "#fbbf24", // yellow
      "#fb7185", // pink
      "#34d399", // emerald
      "#a78bfa", // violet
      "#f59e0b", // amber
      VIOLET_500, // indigo / violet-500
    ],
    // UI state colors
    backdrop: "rgba(0,0,0,0.35)",
    buttonHover: "#f0f0f0",
    white: "#FFFFFF",
    /** Pure black (video / cropper chrome) */
    pureBlack: "#000000",
    /** Auth page gradient + inline alert surfaces (red-50 / teal-50 / green-50 tints) */
    authGradientStart: "#f0fdfa",
    authGradientEnd: "#ecfeff",
    messageErrorBg: "#fef2f2",
    messageErrorBorder: "#fecaca",
    messageSuccessBg: "#f0fdf4",
    /** Welcome hero gradient mid stop (cyan-600) */
    welcomeGradientCyan: "#0891b2",
    /** Tailwind teal-600 / auth accent */
    teal600: "#0d9488",
    /** Tailwind teal-700 / link hover */
    teal700: "#0f766e",
    /** Primary CTA hover (between primary and teal-600) */
    primaryButtonHover: "#018f93",
    /** Input focus ring (teal-600 channel) */
    tealFocusRing: "rgba(13, 148, 136, 0.1)",
    // UI colors
    textDark: "#1a1a1a",
    textStrong: "#424242",
    textSlate: "#374151",
    /** Common #333 body text on light surfaces */
    textCharcoal: "#333333",
    textMedium: "#666",
    // Event card colors
    eventBorder: "#2E7D32",
    eventOverlay: "rgba(34, 145, 80, 0.45)",
    // Gradients
    gradientTealBlue: ["#4ac5b4", "#1e88e5"],
    gradientTealTransparent: ["#4ac5b4", "transparent"],
    gradientEvent: ["#219150", "#166534", "#0B3C26"],
    gradientLight: ["#f3f4f6", "#e5e7eb"],
    /** White → pale-teal backdrop behind page hero/banner cards */
    gradientHeroSurface: ["#ffffff", "#eef9f8"],
    // Shadows
    shadowLight: "rgba(0, 0, 0, 0.05)",
    shadowMedium: "rgba(0, 0, 0, 0.08)",
    shadowDark: "rgba(0, 0, 0, 0.1)",
    /** Elevation shadow color (cards, dots, modals) */
    shadowBlack15: "rgba(0, 0, 0, 0.15)",
    /** Material blue elevation (MUI primary blue) */
    blueTint15: "rgba(25, 118, 210, 0.15)",
    shadowBlack12: "rgba(0, 0, 0, 0.12)",
    shadowTeal: "rgba(74, 197, 180, 0.05)",
    shadowSuccess: "rgba(34, 197, 94, 0.08)",
    shadowWarning: "rgba(245, 158, 11, 0.08)",
    shadowSuccessFocus: "rgba(34, 197, 94, 0.2)",
    // Template preview accent colors
    templateUrgentStrong: "rgba(239, 68, 68, 0.55)",
    templateUrgentLight: "rgba(239, 68, 68, 0.20)",
    templateUrgentPill: "rgba(220, 38, 38, 0.85)",
    templateEventStrong: "rgba(34, 197, 94, 0.55)",
    templateEventLight: "rgba(34, 197, 94, 0.20)",
    templateEventPill: "rgba(22, 163, 74, 0.85)",
    /** Info notice / template preview — brand teal (#0AB3B8) */
    templateInfoStrong: "rgba(10, 179, 184, 0.55)",
    templateInfoLight: "rgba(10, 179, 184, 0.20)",
    templateInfoPill: "#0AB3B8",
    // Border colors
    borderLight: "#e9ecef",
    borderSlate: "rgba(15, 23, 42, 0.10)",
    borderSlateHover: "rgba(15, 23, 42, 0.18)",
    borderSlateLight: "rgba(15, 23, 42, 0.08)",
    // Card background colors (for create notice cards)
    cardBlueBg: "#e2eefc",
    cardBlueHover: "#DBEAFE",
    cardBlueText: "#3B82F6",
    cardPinkBg: "#fbf0f8",
    cardPinkHover: "#F9E6F3",
    cardPinkText: "#F472B6",
    cardTealBg: "#ddf4f0",
    /** Material teal-50 */
    teal50: "#e0f2f1",
    /** Material blue-50 (selection chips) */
    blue50: "#e3f2fd",
    cardTealHover: "#D1F2EB",
    cardTealText: "#71c0bb",
    cardPurpleBg: "#f5e9fa",
    cardPurpleHover: "#F3E5F8",
    cardPurpleText: "#b989e0",
    cardOrangeBg: "#FFFBEB",
    /** Bootstrap-style warning surface */
    warningSurface: "#fff3cd",
    cardOrangeHover: "#FEF3C7",
    /** Templates card on Create notice (and anything that stands for notice Templates) */
    templatesRose: "#C2415D",
    /** Birthdays card on Create notice (and anything that stands for Birthdays) */
    birthdaysMint: "#48BFA2",
    /** Certificates card on Create notice (and anything that stands for Certificates) */
    certificatesGold: "#C79520",
    /** Signatures card on Create notice (and anything that stands for Signatures) */
    signaturesNavy: "#1E3A5F",
    // Create notice hero gradient colors
    heroGradientStart: "#41cbcd",
    heroGradientMid: "#21b9be",
    heroGradientEnd: "#18a8b8",
    // Icon colors
    iconYellow: "#FFEB3B",
    iconYellowGlow: "rgba(255, 235, 59, 0.4)",
    iconYellowGlowStrong: "rgba(255, 235, 59, 0.7)",
    iconGreen: "#4CAF50",
    iconGreenGlow: "rgba(76, 175, 80, 0.3)",
    iconWhiteGlow: "rgba(255, 255, 255, 0.4)",
    // Text shadow colors
    textShadowDark: "rgba(0, 0, 0, 0.3)",
    textShadowMedium: "rgba(0, 0, 0, 0.2)",
    // White overlay colors
    whiteOverlay70: "rgba(255, 255, 255, 0.7)",
    whiteOverlay65: "rgba(255, 255, 255, 0.65)",
    whiteOverlay60: "rgba(255, 255, 255, 0.6)",
    whiteOverlay45: "rgba(255, 255, 255, 0.45)",
    whiteOverlay30: "rgba(255, 255, 255, 0.3)",
    whiteOverlay25: "rgba(255, 255, 255, 0.25)",
    whiteOverlay50: "rgba(255, 255, 255, 0.5)",
    whiteOverlay90: "rgba(255, 255, 255, 0.9)",
    whiteOverlay95: "rgba(255, 255, 255, 0.95)",
    blackOverlay70: "rgba(0, 0, 0, 0.7)",
    blackOverlay80: "rgba(0, 0, 0, 0.8)",
    blackOverlay50: "rgba(0, 0, 0, 0.5)",
    blackOverlay40: "rgba(0, 0, 0, 0.4)",
    blackOverlay60: "rgba(0, 0, 0, 0.6)",
    blackOverlay25: "rgba(0, 0, 0, 0.25)",
    // Grey shades (for MUI grey palette)
    grey50: "#f9fafb",
    grey100: "#f5f5f5",
    grey300: "#d1d5db",
    /** Material grey 400 (disabled surfaces, past events) */
    materialGrey400: "#bdbdbd",
    grey400: "#9ca3af",
    /** Tailwind slate-500 (muted UI text) */
    slate500: "#6b7280",
    slate600: "#4b5563",
    grey500: "#9e9e9e",
    grey600: "#737373",
    grey700: "#94A3B8",
    grey900: "#212121",
  },
  typography: {
    fontFamily:
      "'Raleway', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    fontSize: {
      xs: "0.75rem", // 12px
      sm: "0.875rem", // 14px
      base: "1rem", // 16px
      lg: "1.125rem", // 18px
      xl: "1.25rem", // 20px
      "2xl": "1.5rem", // 24px
      "3xl": "1.875rem", // 30px
      "4xl": "2rem", // 32px
      "4.5xl": "2.5rem", // 40px
      "5xl": "3rem", // 48px
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
    inputFontSize: "1.125rem",
    labelFontSize: "1.25rem",
    errorFontSize: "0.875rem",
  },
  spacing: {
    px: "1px",
    0: "0",
    1: "0.25rem", // 4px
    1.5: "0.375rem", // 6px
    2: "0.5rem", // 8px
    3: "0.75rem", // 12px
    3.5: "0.875rem", // 14px
    4: "1rem", // 16px
    5: "1.25rem", // 20px
    6: "1.5rem", // 24px
    8: "2rem", // 32px
    10: "2.5rem", // 40px
    12: "3rem", // 48px
    16: "4rem", // 64px
    20: "5rem", // 80px
    24: "6rem", // 96px
  },
  borderRadius: {
    none: "0",
    sm: "0.125rem", // 2px
    base: "0.25rem", // 4px
    md: "0.375rem", // 6px
    lg: "0.5rem", // 8px
    xl: "0.75rem", // 12px
    "2xl": "1rem", // 16px
    "3xl": "1.25rem", // 20px
    layout: "1.875rem", // 30px
    full: "9999px",
    input: "12px",
  },
  shadows: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    base: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    input: "0 2px 4px rgba(0, 0, 0, 0.02)",
    inputHover: "0 4px 8px rgba(0, 0, 0, 0.04)",
  },
  dimensions: {
    topNavHeight: "60px",
    listItemHeight: "72px",
    fabSize: "56px",
    headerHeight: "60px",
    headerHeightMobile: "56px",
    headerButtonHeight: "32px",
    headerButtonHeightMobile: "36px",
    headerIconSize: "16px",
    headerIconSizeMobile: "18px",
    mobileNavTopOffset: "8px",
    mobileNavZIndex: 998,
  },
  transitions: {
    mobileNavTransform: "transform 0.3s ease",
    input: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    button: "all 0.2s ease",
  },
};
